/**
 * Goal-Based Asset Allocation Utilities
 * Provides recommendations for asset allocation based on goal time horizons
 */

import { calculateMonthsRemaining } from './goalCalculations';

/**
 * Recommend asset allocation based on goal timeline
 * @param {Object} goal - Financial goal object
 * @returns {Object} Recommended allocation
 */
export const recommendAllocationByGoal = (goal) => {
  if (!goal || !goal.targetDate) {
    return null;
  }

  const monthsRemaining = calculateMonthsRemaining(goal.targetDate);

  // Short-term: 0-36 months
  if (monthsRemaining <= 36) {
    return {
      stocks: 0,
      bonds: 20,
      cash: 80,
      rationale: 'Short-term goal - preserve capital, avoid market volatility',
      riskLevel: 'Very Low',
      timeHorizon: 'short'
    };
  }

  // Medium-term: 37-84 months (3-7 years)
  if (monthsRemaining <= 84) {
    return {
      stocks: 50,
      bonds: 40,
      cash: 10,
      rationale: 'Medium-term goal - balanced growth with downside protection',
      riskLevel: 'Moderate',
      timeHorizon: 'medium'
    };
  }

  // Long-term: 85+ months (7+ years)
  return {
    stocks: 80,
    bonds: 15,
    cash: 5,
    rationale: 'Long-term goal - maximize growth potential, time to weather volatility',
    riskLevel: 'Moderate-High',
    timeHorizon: 'long'
  };
};

/**
 * Create a glide path for a goal (gradual de-risking over time)
 * @param {Object} goal - Financial goal object
 * @returns {Array} Year-by-year allocation recommendations
 */
export const createGlidePath = (goal) => {
  if (!goal || !goal.targetDate) {
    return [];
  }

  const monthsRemaining = calculateMonthsRemaining(goal.targetDate);
  const yearsRemaining = Math.ceil(monthsRemaining / 12);

  // Only create glide path for medium to long-term goals
  if (monthsRemaining <= 36) {
    return [{
      year: 0,
      stocks: 0,
      bonds: 20,
      cash: 80,
      note: 'Too close to goal date - stay conservative'
    }];
  }

  const glidePath = [];
  const startStocks = Math.min(80, yearsRemaining * 10); // More aggressive for longer timelines

  for (let year = 0; year < yearsRemaining; year++) {
    const yearsLeft = yearsRemaining - year;
    let stocks, bonds, cash;

    if (yearsLeft >= 7) {
      stocks = 80;
      bonds = 15;
      cash = 5;
    } else if (yearsLeft >= 5) {
      stocks = 60;
      bonds = 35;
      cash = 5;
    } else if (yearsLeft >= 3) {
      stocks = 40;
      bonds = 50;
      cash = 10;
    } else if (yearsLeft >= 2) {
      stocks = 20;
      bonds = 60;
      cash = 20;
    } else {
      stocks = 0;
      bonds = 30;
      cash = 70;
    }

    glidePath.push({
      year: year + 1,
      yearsToGoal: yearsLeft,
      stocks,
      bonds,
      cash,
      note: getGlidePathNote(yearsLeft)
    });
  }

  return glidePath;
};

/**
 * Get explanatory note for glide path year
 * @param {number} yearsLeft - Years remaining to goal
 * @returns {string} Explanation
 */
const getGlidePathNote = (yearsLeft) => {
  if (yearsLeft >= 7) return 'Growth phase - maximize returns';
  if (yearsLeft >= 5) return 'Transition phase - start reducing risk';
  if (yearsLeft >= 3) return 'De-risking phase - preserve gains';
  if (yearsLeft >= 2) return 'Conservative phase - protect capital';
  return 'Final approach - prioritize stability';
};

/**
 * Categorize goals by time horizon
 * @param {Array} goals - Array of financial goals
 * @returns {Object} Goals grouped by time horizon
 */
export const categorizeGoalsByTimeHorizon = (goals) => {
  if (!goals || goals.length === 0) {
    return { short: [], medium: [], long: [] };
  }

  const categorized = { short: [], medium: [], long: [] };

  goals.forEach(goal => {
    const monthsRemaining = calculateMonthsRemaining(goal.targetDate);

    if (monthsRemaining <= 36) {
      categorized.short.push(goal);
    } else if (monthsRemaining <= 84) {
      categorized.medium.push(goal);
    } else {
      categorized.long.push(goal);
    }
  });

  return categorized;
};

/**
 * Detect potential goal conflicts (timing or cash flow)
 * @param {Array} goals - Array of financial goals
 * @param {number} monthlyIncome - User's monthly income (optional)
 * @returns {Array} Detected conflicts
 */
export const detectGoalConflicts = (goals, monthlyIncome = null) => {
  if (!goals || goals.length === 0) {
    return [];
  }

  const conflicts = [];

  // Group goals by target year
  const goalsByYear = {};
  goals.forEach(goal => {
    const targetDate = new Date(goal.targetDate);
    const year = targetDate.getFullYear();

    if (!goalsByYear[year]) {
      goalsByYear[year] = [];
    }
    goalsByYear[year].push(goal);
  });

  // Check for multiple goals in same year
  Object.entries(goalsByYear).forEach(([year, yearGoals]) => {
    if (yearGoals.length > 1) {
      const totalTarget = yearGoals.reduce((sum, g) =>
        sum + (parseFloat(g.targetAmount) || 0), 0
      );

      conflicts.push({
        type: 'timing',
        year: parseInt(year),
        goals: yearGoals.map(g => ({ name: g.name, type: g.type })),
        issue: `Multiple goals targeted for ${year}`,
        totalAmount: totalTarget,
        recommendation: 'Consider staggering goal timelines or prioritizing'
      });
    }
  });

  // Check for cash flow conflicts (if income provided)
  if (monthlyIncome && monthlyIncome > 0) {
    const totalMonthlyContributions = goals.reduce((sum, g) =>
      sum + (parseFloat(g.monthlyContribution) || 0), 0
    );

    const savingsRate = (totalMonthlyContributions / monthlyIncome) * 100;

    if (savingsRate > 50) {
      conflicts.push({
        type: 'cashflow',
        issue: 'Total goal contributions exceed 50% of income',
        monthlyRequired: totalMonthlyContributions,
        monthlyIncome: monthlyIncome,
        savingsRate: savingsRate,
        recommendation: 'Reduce goal targets, extend timelines, or increase income'
      });
    }
  }

  // Check for underfunded short-term goals
  goals.forEach(goal => {
    const monthsRemaining = calculateMonthsRemaining(goal.targetDate);
    const currentAmount = parseFloat(goal.currentAmount) || 0;
    const targetAmount = parseFloat(goal.targetAmount) || 0;
    const monthlyContribution = parseFloat(goal.monthlyContribution) || 0;

    if (monthsRemaining <= 36 && monthsRemaining > 0) {
      const projectedAmount = currentAmount + (monthlyContribution * monthsRemaining);

      if (projectedAmount < targetAmount * 0.8) {
        conflicts.push({
          type: 'underfunded',
          goal: { name: goal.name, type: goal.type },
          monthsRemaining,
          shortfall: targetAmount - projectedAmount,
          currentProgress: (currentAmount / targetAmount) * 100,
          recommendation: `Increase monthly contribution or extend timeline`
        });
      }
    }
  });

  return conflicts;
};

/**
 * Calculate recommended emergency fund amount
 * @param {number} monthlyExpenses - User's monthly expenses
 * @param {string} employmentType - 'stable' | 'variable' | 'self-employed'
 * @param {number} dependents - Number of dependents
 * @returns {Object} Emergency fund recommendation
 */
export const calculateEmergencyFundRecommendation = (
  monthlyExpenses,
  employmentType = 'stable',
  dependents = 0
) => {
  if (!monthlyExpenses || monthlyExpenses <= 0) {
    return null;
  }

  let recommendedMonths = 6; // Default

  // Adjust based on employment type
  if (employmentType === 'self-employed') {
    recommendedMonths = 12;
  } else if (employmentType === 'variable') {
    recommendedMonths = 9;
  } else if (employmentType === 'stable') {
    recommendedMonths = 6;
  }

  // Adjust for dependents
  if (dependents > 0) {
    recommendedMonths += Math.min(dependents * 2, 6); // Max 6 extra months
  }

  const recommendedAmount = monthlyExpenses * recommendedMonths;

  return {
    monthlyExpenses,
    recommendedMonths,
    recommendedAmount,
    minimumAmount: monthlyExpenses * 3,
    conservativeAmount: monthlyExpenses * 12,
    rationale: getRationale(employmentType, dependents, recommendedMonths)
  };
};

/**
 * Get rationale for emergency fund recommendation
 * @param {string} employmentType - Employment type
 * @param {number} dependents - Number of dependents
 * @param {number} months - Recommended months
 * @returns {string} Explanation
 */
const getRationale = (employmentType, dependents, months) => {
  let reason = `${months} months recommended based on `;

  const factors = [];

  if (employmentType === 'self-employed') {
    factors.push('self-employment (irregular income)');
  } else if (employmentType === 'variable') {
    factors.push('variable income');
  } else {
    factors.push('stable employment');
  }

  if (dependents > 0) {
    factors.push(`${dependents} dependent${dependents > 1 ? 's' : ''}`);
  }

  return reason + factors.join(' and ');
};

/**
 * Assess if current allocation matches goal's recommended allocation
 * @param {Object} goal - Financial goal
 * @param {Object} currentAllocation - Current portfolio allocation { stocks, bonds, cash }
 * @returns {Object} Risk mismatch assessment
 */
export const assessRiskMismatch = (goal, currentAllocation) => {
  if (!goal || !currentAllocation) {
    return null;
  }

  const recommended = recommendAllocationByGoal(goal);
  if (!recommended) return null;

  const stocksDiff = Math.abs(currentAllocation.stocks - recommended.stocks);
  const bondsDiff = Math.abs(currentAllocation.bonds - recommended.bonds);
  const cashDiff = Math.abs(currentAllocation.cash - recommended.cash);

  const totalDiff = stocksDiff + bondsDiff + cashDiff;

  let severity = 'none';
  let warning = null;

  if (totalDiff > 40) {
    severity = 'high';
    warning = 'Significant mismatch - portfolio risk not aligned with goal timeline';
  } else if (totalDiff > 20) {
    severity = 'medium';
    warning = 'Moderate mismatch - consider adjusting allocation';
  } else if (totalDiff > 10) {
    severity = 'low';
    warning = 'Minor mismatch - mostly aligned';
  }

  return {
    severity,
    warning,
    recommended,
    current: currentAllocation,
    adjustments: {
      stocks: recommended.stocks - currentAllocation.stocks,
      bonds: recommended.bonds - currentAllocation.bonds,
      cash: recommended.cash - currentAllocation.cash
    }
  };
};

/**
 * Generate bucket strategy allocation
 * @param {Array} goals - Array of financial goals
 * @param {number} totalPortfolioValue - Total portfolio value
 * @returns {Object} Bucket strategy breakdown
 */
export const generateBucketStrategy = (goals, totalPortfolioValue) => {
  if (!goals || goals.length === 0 || !totalPortfolioValue) {
    return null;
  }

  const categorized = categorizeGoalsByTimeHorizon(goals);

  const shortTermTarget = categorized.short.reduce((sum, g) =>
    sum + (parseFloat(g.targetAmount) || 0), 0
  );

  const mediumTermTarget = categorized.medium.reduce((sum, g) =>
    sum + (parseFloat(g.targetAmount) || 0), 0
  );

  const longTermTarget = categorized.long.reduce((sum, g) =>
    sum + (parseFloat(g.targetAmount) || 0), 0
  );

  const totalTarget = shortTermTarget + mediumTermTarget + longTermTarget;

  return {
    bucket1_shortTerm: {
      timeHorizon: '0-3 years',
      goals: categorized.short,
      targetAmount: shortTermTarget,
      recommendedAllocation: { stocks: 0, bonds: 20, cash: 80 },
      portfolioPercentage: totalTarget > 0 ? (shortTermTarget / totalTarget * 100).toFixed(1) : 0
    },
    bucket2_mediumTerm: {
      timeHorizon: '3-7 years',
      goals: categorized.medium,
      targetAmount: mediumTermTarget,
      recommendedAllocation: { stocks: 50, bonds: 40, cash: 10 },
      portfolioPercentage: totalTarget > 0 ? (mediumTermTarget / totalTarget * 100).toFixed(1) : 0
    },
    bucket3_longTerm: {
      timeHorizon: '7+ years',
      goals: categorized.long,
      targetAmount: longTermTarget,
      recommendedAllocation: { stocks: 80, bonds: 15, cash: 5 },
      portfolioPercentage: totalTarget > 0 ? (longTermTarget / totalTarget * 100).toFixed(1) : 0
    },
    totalTargetAmount: totalTarget,
    currentPortfolioValue: totalPortfolioValue
  };
};
